import React, { useState } from 'react';
import { mod } from './index';

// Rock Paper Scissors — real PvP, blind submission. Each player picks 5
// throws (rock / paper / scissors / wildcard). Picks are submitted blind;
// once both have submitted, the rounds are compared head-to-head and the
// winner is whoever has more round wins.
//
// CHA hook = number of wildcards available to spend across the 5 rounds.
// A wildcard is a guaranteed-win throw, modelling the charismatic player
// reading their opponent's tells.
//   modifier diff <= 1   → 0 wildcards
//   modifier diff 2-3    → 1 wildcard
//   modifier diff >= 4   → 2 wildcards

const ROUNDS = 5;
const ICON   = { rock: '🪨', paper: '📄', scissors: '✂️', wild: '⭐' };
const LABEL  = { rock: 'Rock', paper: 'Paper', scissors: 'Scissors', wild: 'Wild' };
// Standard rules: rock beats scissors, scissors beats paper, paper beats rock.
const BEATS  = { rock: 'scissors', paper: 'rock', scissors: 'paper' };

function wildcardsFor(myMod, theirMod) {
  const diff = myMod - theirMod;
  if (diff >= 4) return 2;
  if (diff >= 2) return 1;
  return 0;
}

// Round outcome for one side. Returns 'win' | 'lose' | 'tie'.
function roundOutcome(my, their) {
  if (my === 'wild' && their === 'wild') return 'tie';
  if (my === 'wild') return 'win';
  if (their === 'wild') return 'lose';
  if (my === their) return 'tie';
  if (BEATS[my] === their) return 'win';
  return 'lose';
}

function countWins(myPicks, theirPicks) {
  let wins = 0;
  for (let i = 0; i < ROUNDS; i++) {
    if (roundOutcome(myPicks?.[i], theirPicks?.[i]) === 'win') wins++;
  }
  return wins;
}

export default {
  id: 'rockPaperScissors',
  label: 'Rock Paper Scissors',
  stat: 'cha',
  icon: '✂️',
  description: 'Blind-pick 5 throws. Highest CHA gets wildcards (auto-win throws).',
  paramsFromStats(myScore, theirScore) {
    return { wildcards: wildcardsFor(mod(myScore), mod(theirScore)) };
  },
  computeWinner(fromScore, toScore) {
    const fromPicks = fromScore?.picks || [];
    const toPicks   = toScore?.picks   || [];
    const fromWins  = countWins(fromPicks, toPicks);
    const toWins    = countWins(toPicks,   fromPicks);
    if (fromWins > toWins) return 'from';
    if (toWins > fromWins) return 'to';
    return 'tie';
  },
  scoreNumber(score) {
    // Rough "wins out of 5" surrogate so the default scoreboard has something
    // to show. Real result UI is ResultDisplay below.
    return score?.picks ? score.picks.length : null;
  },
  ResultDisplay({ challenge, mySide }) {
    const opponentName = mySide === 'from' ? challenge.toName : challenge.fromName;
    const myScore       = mySide === 'from' ? challenge.fromScore : challenge.toScore;
    const oppScore      = mySide === 'from' ? challenge.toScore   : challenge.fromScore;
    const myPicks       = myScore?.picks  || [];
    const oppPicks      = oppScore?.picks || [];

    return (
      <div style={resultStyles.wrap}>
        <div style={resultStyles.row}>
          <div style={resultStyles.colHeader}>You</div>
          <div style={resultStyles.colHeader} />
          <div style={resultStyles.colHeader}>{opponentName}</div>
        </div>
        {Array.from({ length: ROUNDS }).map((_, i) => {
          const my  = myPicks[i];
          const opp = oppPicks[i];
          const out = roundOutcome(my, opp);
          return (
            <div key={i} style={resultStyles.row}>
              <div style={{ ...resultStyles.cell, ...(out === 'win' ? resultStyles.cellWin : out === 'lose' ? resultStyles.cellLose : {}) }}>
                <span style={resultStyles.icon}>{ICON[my] || '·'}</span>
                <span style={resultStyles.label}>{LABEL[my] || ''}</span>
              </div>
              <div style={resultStyles.middle}>
                {out === 'win'  && <span style={{ ...resultStyles.tag, ...resultStyles.tagWin }}>WIN</span>}
                {out === 'lose' && <span style={{ ...resultStyles.tag, ...resultStyles.tagLose }}>LOSE</span>}
                {out === 'tie'  && <span style={{ ...resultStyles.tag, ...resultStyles.tagTie }}>TIE</span>}
              </div>
              <div style={{ ...resultStyles.cell, ...(out === 'lose' ? resultStyles.cellWin : out === 'win' ? resultStyles.cellLose : {}) }}>
                <span style={resultStyles.icon}>{ICON[opp] || '·'}</span>
                <span style={resultStyles.label}>{LABEL[opp] || ''}</span>
              </div>
            </div>
          );
        })}
        <div style={resultStyles.totalsRow}>
          <span style={resultStyles.totalSide}>Wins: {countWins(myPicks, oppPicks)}</span>
          <span style={resultStyles.totalSide}>Wins: {countWins(oppPicks, myPicks)}</span>
        </div>
      </div>
    );
  },
  Component({ challenge, mySide, params, seed, onScore }) {
    const wildcardCap = params?.wildcards || 0;
    const [picks, setPicks] = useState(Array(ROUNDS).fill(null));
    const [submitted, setSubmitted] = useState(false);

    const wildsUsed = picks.filter((p) => p === 'wild').length;
    const wildsLeft = wildcardCap - wildsUsed;
    const filled    = picks.filter((p) => p != null).length;

    const setRound = (round, choice) => {
      if (submitted) return;
      // Block selecting more wildcards than allowed.
      if (choice === 'wild' && picks[round] !== 'wild' && wildsLeft <= 0) return;
      setPicks((prev) => {
        const next = [...prev];
        next[round] = choice;
        return next;
      });
    };

    const handleSubmit = () => {
      if (submitted) return;
      if (filled < ROUNDS) return;
      setSubmitted(true);
      onScore({ picks });
    };

    return (
      <div style={styles.wrap}>
        <div style={styles.title}>Rock Paper Scissors</div>
        <div style={styles.subtitle}>
          Pick 5 throws. Both players submit blind, then we compare round by round.
        </div>
        {wildcardCap > 0 && (
          <div style={styles.wildBar}>
            <span style={styles.wildLabel}>CHA wildcards</span>
            <span style={styles.wildPills}>
              {Array.from({ length: wildcardCap }).map((_, i) => (
                <span key={i} style={{ ...styles.wildPill, ...(i < wildsUsed ? styles.wildPillSpent : {}) }}>⭐</span>
              ))}
            </span>
            <span style={styles.wildHint}>{wildsLeft} left · auto-wins the round</span>
          </div>
        )}

        <div style={styles.roundsWrap}>
          {picks.map((pick, round) => (
            <div key={round} style={styles.roundRow}>
              <span style={styles.roundLabel}>R{round + 1}</span>
              <div style={styles.choicesRow}>
                {['rock', 'paper', 'scissors'].map((c) => (
                  <button
                    key={c}
                    style={{ ...styles.choiceBtn, ...(pick === c ? styles.choiceBtnActive : {}) }}
                    onClick={() => setRound(round, c)}
                    disabled={submitted}
                  >
                    <span style={styles.choiceIcon}>{ICON[c]}</span>
                  </button>
                ))}
                {wildcardCap > 0 && (
                  <button
                    style={{
                      ...styles.choiceBtn,
                      ...styles.choiceWild,
                      ...(pick === 'wild' ? styles.choiceWildActive : {}),
                      ...(pick !== 'wild' && wildsLeft <= 0 ? styles.choiceWildDisabled : {}),
                    }}
                    onClick={() => setRound(round, 'wild')}
                    disabled={submitted || (pick !== 'wild' && wildsLeft <= 0)}
                    title="Wildcard — auto-wins this round"
                  >
                    <span style={styles.choiceIcon}>{ICON.wild}</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <button
          style={{ ...styles.submitBtn, ...((filled < ROUNDS || submitted) ? styles.submitBtnDisabled : {}) }}
          onClick={handleSubmit}
          disabled={filled < ROUNDS || submitted}
        >
          {submitted ? 'Submitted' : filled < ROUNDS ? `Pick ${ROUNDS - filled} more` : 'Submit picks'}
        </button>
      </div>
    );
  },
};

const styles = {
  wrap:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 14 },
  title:     { fontSize: 22, fontWeight: 800, color: '#d4a847', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.06em' },
  subtitle:  { fontSize: 12, color: '#9a8b6e', textAlign: 'center', maxWidth: 360 },
  wildBar:   { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'rgba(212,168,71,0.1)', border: '1px solid rgba(212,168,71,0.4)', borderRadius: 4, fontFamily: "'Press Start 2P', monospace" },
  wildLabel: { fontSize: 10, color: '#d4a847', letterSpacing: '0.06em', fontWeight: 700, textTransform: 'uppercase' },
  wildPills: { display: 'flex', gap: 3 },
  wildPill:  { fontSize: 14, opacity: 1, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' },
  wildPillSpent: { opacity: 0.25, filter: 'grayscale(1)' },
  wildHint:  { fontSize: 10, color: '#9a8b6e' },

  roundsWrap:{ display: 'flex', flexDirection: 'column', gap: 6, width: 'min(380px, 92vw)' },
  roundRow:  { display: 'flex', alignItems: 'center', gap: 8, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, padding: '4px 8px' },
  roundLabel:{ fontSize: 10, fontWeight: 800, color: '#9a8b6e', letterSpacing: '0.06em', minWidth: 22 },
  choicesRow:{ display: 'flex', gap: 4, flex: 1, justifyContent: 'space-around' },
  choiceBtn: { width: 48, height: 48, background: '#142038', border: '2px solid #4a3d28', borderRadius: 6, color: '#f0e6d0', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  choiceBtnActive: { borderColor: '#d4a847', background: 'rgba(212,168,71,0.15)', boxShadow: '0 0 8px rgba(212,168,71,0.4)' },
  choiceWild:        { background: 'rgba(122,87,184,0.15)', borderColor: 'rgba(122,87,184,0.4)' },
  choiceWildActive:  { borderColor: '#a987d4', background: 'rgba(169,135,212,0.25)', boxShadow: '0 0 8px rgba(169,135,212,0.5)' },
  choiceWildDisabled:{ opacity: 0.25, cursor: 'not-allowed' },
  choiceIcon:{ fontSize: 24, lineHeight: 1 },

  submitBtn:  { padding: '12px 32px', background: 'linear-gradient(180deg, #d4a847, #a68330)', border: '2px solid #c4a44a', color: '#1a0e00', fontSize: 14, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 6, letterSpacing: '0.04em' },
  submitBtnDisabled: { opacity: 0.45, cursor: 'not-allowed', boxShadow: 'none' },
};

const resultStyles = {
  wrap:      { display: 'flex', flexDirection: 'column', gap: 4, padding: '0 8px', width: 'min(420px, 92vw)' },
  row:       { display: 'grid', gridTemplateColumns: '1fr 60px 1fr', alignItems: 'center', gap: 4 },
  colHeader: { fontSize: 11, fontWeight: 700, color: '#9a8b6e', letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'center', padding: 2 },
  cell:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '6px 4px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4 },
  cellWin:   { borderColor: '#5cb870', background: 'rgba(74,154,90,0.12)' },
  cellLose:  { borderColor: '#c03333', background: 'rgba(192,57,43,0.12)', opacity: 0.7 },
  icon:      { fontSize: 22, lineHeight: 1 },
  label:     { fontSize: 9, color: '#9a8b6e', letterSpacing: '0.04em', textTransform: 'uppercase' },
  middle:    { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  tag:       { fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 3, fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.06em' },
  tagWin:    { background: '#5cb870', color: '#0a1a10' },
  tagLose:   { background: '#c03333', color: '#fff' },
  tagTie:    { background: '#4a3d28', color: '#9a8b6e' },
  totalsRow: { display: 'grid', gridTemplateColumns: '1fr 60px 1fr', marginTop: 6, paddingTop: 6, borderTop: '1px solid #4a3d28' },
  totalSide: { fontSize: 13, fontWeight: 800, color: '#d4a847', textAlign: 'center', fontFamily: "'Press Start 2P', monospace" },
};
